import { useEffect, useState, useMemo } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import { clientData, contractData, applicationData } from '../../types/interfaces';
import { getContracts } from '../../services/ApplicationService';
import { hasData, isSuccessfulResponse } from '../../utils/Utils';
import SignersReadOnly from './InstrumentationReadOnlyComponents/SignersReadOnly';
import SummaryApplication from '../Origination/Summary/Application';
import DocumentsReadOnly from './InstrumentationReadOnlyComponents/DocumentsReadOnly';
import ContractsReadOnly from './InstrumentationReadOnlyComponents/ContractsReadOnly';
import SgrApprovalDistributionReadOnly from '../Committee/SgrApprovalDistributionReadOnly';
import { formatCurrency } from '../../utils/Masks';
import { CURRENCY_CODES } from '../../utils/Constants';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';
import styled from 'styled-components';
import { useClientTheme } from '../../context/ThemeContext';
import { useConfig } from '../../context/ConfigContext';

const ResponsiveContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const TableContainer = styled.div`
  flex: 0 0 40%;
  min-width: 0;
  width: 40%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    min-width: 100%;
  }
`;

const SummaryContainer = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    min-width: 100%;
  }
`;

const SummaryCardWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  word-wrap: break-word;
  white-space: normal;
  gap: 8px;
  padding: 16px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

interface InstrumentationReadOnlyViewProps {
  clientData: clientData;
  applicationId: string | undefined;
  cuit: string | undefined;
}

const InstrumentationReadOnlyView: React.FC<InstrumentationReadOnlyViewProps> = ({
  clientData,
  applicationId,
  cuit,
}) => {
  const { clientTheme } = useClientTheme();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const [contracts, setContracts] = useState<contractData[]>([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState<boolean>(true);

  // Calcular totales local + USD de los productos aprobados
  const totals = useMemo(() => {
    let totalLocal = 0;
    let totalUsd = 0;
    if (clientData.approved_products) {
      clientData.approved_products.forEach((product: applicationData) => {
        const value = Number(product.amount.value);
        if (product.amount.currency_code === localCurrencyCode) {
          totalLocal += value;
        } else if (
          product.amount.currency_code === CURRENCY_CODES.usd ||
          product.amount.currency_code === 'USD'
        ) {
          totalUsd += value;
        }
      });
    }
    return { totalLocal, totalUsd };
  }, [clientData.approved_products, localCurrencyCode]);

  useEffect(() => {
    if (applicationId) {
      setIsLoadingContracts(true);
      getContracts(applicationId)
        .then((response) => {
          if (hasData(response) && isSuccessfulResponse(response)) {
            setContracts(response.data || []);
          }
        })
        .finally(() => {
          setIsLoadingContracts(false);
        });
    }
  }, [applicationId]);

  return (
    <Card spacing={{ top: '4' }}>
      {/* Sección Firmantes */}
      <SignersReadOnly
        signers={clientData.signers || []}
        cuit={cuit}
        applicationId={applicationId}
      />

      {/* Sección Montos aprobados */}
      <Card margin={{ top: '4' }}>
        <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
          <H
            type='3'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            Montos aprobados
          </H>
        </Card>
        {clientData.reason && (
          <Card margin={{ top: '2' }}>
            <P type='2' variant='neutral' withMargin={false}>
              Motivo: {clientData.reason || '--'}
            </P>
          </Card>
        )}
        {!clientData.reason && (
          <Card margin={{ top: '2' }}>
            <P type='2' variant='neutral' withMargin={false}>
              Motivo: --
            </P>
          </Card>
        )}
        {clientData.approved_products && clientData.approved_products.length > 0 ? (
          <Card margin={{ top: '4' }}>
            <ResponsiveContainer>
              <TableContainer>
                <SummaryApplication applications={clientData.approved_products} />
              </TableContainer>
              <SummaryContainer>
                <Card 
                  border={true} 
                  variant={clientTheme ? 'primary' : 'success'} 
                  modifier='weak' 
                  spacing={'2'}
                  width='100%'
                  height='100%'
                >
                  <SummaryCardWrapper>
                    <ColumnContainer>
                      <H
                        type='2'
                        withMargin={false}
                        weight='bold'
                        variant={clientTheme ? 'primary' : 'success'}
                        align='center'
                      >
                        {localCurrencyCode} {formatCurrency(totals.totalLocal.toString())}
                      </H>
                      {totals.totalUsd > 0 && (
                        <H
                          type='2'
                          withMargin={false}
                          weight='bold'
                          variant={clientTheme ? 'primary' : 'success'}
                          align='center'
                        >
                          {CURRENCY_CODES.usd} {formatCurrency(totals.totalUsd.toString())}
                        </H>
                      )}
                      <H
                        type='2'
                        withMargin={false}
                        weight='bold'
                        variant={clientTheme ? 'primary' : 'success'}
                        align='center'
                      >
                        Valor fianza {formatCurrency(clientData.note_amount?.toString() || totals.totalLocal.toString())}
                      </H>
                      {totals.totalUsd > 0 && (
                        <H
                          type='2'
                          withMargin={false}
                          weight='bold'
                          variant={clientTheme ? 'primary' : 'success'}
                          align='center'
                        >
                          Valor pagaré {formatCurrency(clientData.guarantee_amount?.toString() || totals.totalUsd.toString())}
                        </H>
                      )}
                    </ColumnContainer>
                  </SummaryCardWrapper>
                </Card>
              </SummaryContainer>
            </ResponsiveContainer>
          </Card>
        ) : (
          <Card margin={{ top: '4' }}>
            <P type='2' variant='neutral' modifier='weak' withMargin={false}>
              No hay montos aprobados
            </P>
          </Card>
        )}
        {clientData.sgr_distributions && clientData.sgr_distributions.length > 0 && (
          <SgrApprovalDistributionReadOnly
            sgrDistributions={clientData.sgr_distributions}
          />
        )}
      </Card>

      {/* Sección Documentación */}
      <DocumentsReadOnly
        clientData={clientData}
        cuit={cuit}
        applicationId={applicationId}
      />

      {/* Sección Contratos */}
      <ContractsReadOnly
        contracts={contracts}
        isLoading={isLoadingContracts}
        cuit={cuit}
        applicationId={applicationId}
      />
    </Card>
  );
};

export default InstrumentationReadOnlyView;

